// src/admin/components/POCreateDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import SupplierDialog from '../supplier/SupplierDialog';
import { PurchaseOrderService } from './purchaseOrderService';
import { ProductService } from '../product/productService';
import { Supplier, PurchaseOrderItem } from './PurchaseOrder';
import { Product, ProductVariant } from '../../../enums/product';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { useCompany } from '../../../contexts/CompanyContext';
import { formatCurrency } from '../../../util/Formatcurrency';

interface POCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const POCreateDialog: React.FC<POCreateDialogProps> = ({ open, onClose, onSuccess, onShowToast }) => {
  const { currentUser } = useAuth();
  const { companyProfile } = useCompany();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [items, setItems] = useState<PurchaseOrderItem[]>([]);
  const [taxRate, setTaxRate] = useState('0');
  const [shippingCost, setShippingCost] = useState('0');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [notes, setNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSuppliers();
      fetchProducts();
    }
  }, [open]);

  const fetchSuppliers = async () => {
    try {
      const data = await PurchaseOrderService.getAllSuppliers();
      setSuppliers(data);
    } catch (err: any) {
      onShowToast('Failed to load suppliers', 'error');
    }
  };

  const fetchProducts = async () => {
    try {
      const data = await ProductService.getProductsForDropdown();
      setProducts(data);
    } catch (err: any) {
      onShowToast('Failed to load products', 'error');
    }
  };

  const handleAddItem = () => {
    setItems([...items, {
      id: `temp-${Date.now()}`,
      itemType: 'product',
      productId: '', productName: '', variantId: '', variantName: '',
      sku: '', unit: '', quantity: 1, unitPrice: 0, totalPrice: 0, receivedQuantity: 0,
    }]);
  };

  // Raw material / other supply — entered free-form, not tied to the catalogue.
  const handleAddRawMaterial = () => {
    setItems([...items, {
      id: `temp-${Date.now()}`,
      itemType: 'raw_material',
      productId: '', productName: '', variantId: '', variantName: '',
      sku: '', unit: '', quantity: 1, unitPrice: 0, totalPrice: 0, receivedQuantity: 0,
    }]);
  };

  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleItemChange = (index: number, field: keyof PurchaseOrderItem, value: any) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].totalPrice = newItems[index].quantity * newItems[index].unitPrice;
    }
    setItems(newItems);
  };

  const handleProductSelect = (index: number, product: Product | null) => {
    const newItems = [...items];
    if (!product) {
      newItems[index] = { ...newItems[index], productId: '', productName: '', variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
      setItems(newItems);
      return;
    }
    if (!product.hasVariants || !product.variants || product.variants.length === 0) {
      // Default the PO's unit price to the product's purchase/production cost
      // (unitCost), not its selling price — see answers.text: pre-filling from
      // product.price silently polluted purchase cost data.
      const defaultUnitPrice = product.unitCost || product.price;
      newItems[index] = { ...newItems[index], productId: product.id!, productName: product.name, variantId: '', variantName: '', sku: product.sku, unitPrice: defaultUnitPrice, totalPrice: newItems[index].quantity * defaultUnitPrice };
    } else {
      newItems[index] = { ...newItems[index], productId: product.id!, productName: product.name, variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
    }
    setItems(newItems);
  };

  const handleVariantSelect = (index: number, variant: ProductVariant | null) => {
    const newItems = [...items];
    if (!variant) {
      newItems[index] = { ...newItems[index], variantId: '', variantName: '', sku: '', unitPrice: 0, totalPrice: 0 };
      setItems(newItems);
      return;
    }
    const variantName = variant.variantSelections.map(vs => vs.variantValue).join(' - ');
    // Same cost-not-price default as handleProductSelect above.
    const defaultUnitPrice = variant.unitCost || variant.price;
    newItems[index] = { ...newItems[index], variantId: variant.id || '', variantName, sku: variant.sku, unitPrice: defaultUnitPrice, totalPrice: newItems[index].quantity * defaultUnitPrice };
    setItems(newItems);
  };

  const getSelectedProduct = (index: number): Product | undefined => products.find(p => p.id === items[index].productId);

  const getProductVariants = (index: number): ProductVariant[] => {
    const product = getSelectedProduct(index);
    if (!product || !product.hasVariants || !product.variants) return [];
    return product.variants;
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0);
    const tax = subtotal * (parseFloat(taxRate) / 100);
    const shipping = parseFloat(shippingCost) || 0;
    return { subtotal, tax, shipping, total: subtotal + tax + shipping };
  };

  const handleSubmit = async () => {
    if (!currentUser) { onShowToast('You must be logged in to create purchase orders', 'error'); return; }
    if (!selectedSupplier) { setError('Please select a supplier'); onShowToast('Please select a supplier', 'error'); return; }
    if (items.length === 0) { setError('Please add at least one item'); onShowToast('Please add at least one item', 'error'); return; }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.itemType === 'raw_material') {
        if (!item.productName.trim()) { const msg = `Please enter a name for raw material item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
      } else {
        if (!item.productId) { const msg = `Please select a product for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
        const product = products.find(p => p.id === item.productId);
        if (product && product.hasVariants && product.variants && product.variants.length > 0 && !item.variantId) {
          const msg = `Please select a variant for item #${i + 1} (${item.productName})`;
          setError(msg); onShowToast(msg, 'error'); return;
        }
      }
      if (item.quantity <= 0) { const msg = `Please enter a valid quantity for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
      if (item.unitPrice <= 0) { const msg = `Please enter a valid unit price for item #${i + 1}`; setError(msg); onShowToast(msg, 'error'); return; }
    }

    const tax = parseFloat(taxRate);
    if (tax < 0 || tax > 100) { setError('Tax rate must be between 0 and 100'); onShowToast('Tax rate must be between 0 and 100', 'error'); return; }
    const shipping = parseFloat(shippingCost);
    if (shipping < 0) { setError('Shipping cost cannot be negative'); onShowToast('Shipping cost cannot be negative', 'error'); return; }

    try {
      setLoading(true);
      setError('');
      await PurchaseOrderService.createPurchaseOrder({
        supplierId: selectedSupplier.id!,
        items, taxRate, shippingCost,
        expectedDeliveryDate: expectedDeliveryDate ? new Date(expectedDeliveryDate) : null,
        notes, internalNotes,
      }, currentUser.uid, currentUser.displayName || currentUser.email || 'Unknown');
      onShowToast('Purchase order created successfully', 'success');
      onSuccess();
      handleClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to create purchase order';
      setError(errorMessage);
      onShowToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedSupplier(null);
    setItems([]);
    setTaxRate('0');
    setShippingCost('0');
    setExpectedDeliveryDate('');
    setNotes('');
    setInternalNotes('');
    setError('');
    onClose();
  };

  const handleSupplierDialogSuccess = async () => {
    await fetchSuppliers();
    setSupplierDialogOpen(false);
    onShowToast('Supplier created successfully', 'success');
  };

  const totals = calculateTotals();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: '100%', md: '85vw', lg: 1100 }, maxWidth: '100vw' } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

          {/* Header */}
          <Box sx={{
            px: 3, py: 2,
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexShrink: 0,
          }}>
            <Typography variant="h6">Create Purchase Order</Typography>
            <IconButton onClick={handleClose} edge="end" disabled={loading}><CloseIcon /></IconButton>
          </Box>

          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
            )}

            <Grid container spacing={3}>
              {/* Supplier */}
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="flex-start">
                  <Autocomplete
                    options={suppliers}
                    getOptionLabel={(option) => option.name}
                    value={selectedSupplier}
                    onChange={(_, value) => setSelectedSupplier(value)}
                    renderInput={(params) => <TextField {...params} label="Supplier *" placeholder="Select supplier" />}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box>
                          <Typography variant="body2">{option.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{option.email} | {option.phone}</Typography>
                        </Box>
                      </li>
                    )}
                    sx={{ flex: 1 }}
                    disabled={loading}
                  />
                  <Button variant="outlined" onClick={() => setSupplierDialogOpen(true)} sx={{ minWidth: 120, height: { sm: 56 }, width: { xs: '100%', sm: 'auto' } }} disabled={loading}>
                    New Supplier
                  </Button>
                </Stack>
              </Grid>

              {/* Items Section */}
              <Grid item xs={12}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Order Items</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button startIcon={<AddIcon />} onClick={handleAddRawMaterial} size="small" variant="outlined" color="secondary" disabled={loading}>
                      Add Raw Material
                    </Button>
                    <Button startIcon={<AddIcon />} onClick={handleAddItem} size="small" variant="outlined" disabled={loading}>
                      Add Product
                    </Button>
                  </Stack>
                </Stack>

                <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Product / Material</TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Variant / Unit</TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>SKU</TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>Total</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => {
                        const isRawMaterial = item.itemType === 'raw_material';
                        const selectedProduct = getSelectedProduct(index);
                        const hasVariants = !isRawMaterial && selectedProduct?.hasVariants && selectedProduct?.variants && selectedProduct.variants.length > 0;
                        const variants = getProductVariants(index);

                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              {isRawMaterial ? (
                                <TextField
                                  size="small"
                                  placeholder="Raw material name"
                                  value={item.productName}
                                  onChange={(e) => handleItemChange(index, 'productName', e.target.value)}
                                  sx={{ minWidth: { xs: 140, sm: 180 } }}
                                  disabled={loading}
                                  InputProps={{ startAdornment: <Chip label="Raw" size="small" color="secondary" sx={{ mr: 1, height: 20 }} /> }}
                                />
                              ) : (
                              <Autocomplete
                                options={products}
                                getOptionLabel={(option) => option.name}
                                value={selectedProduct || null}
                                onChange={(_, value) => handleProductSelect(index, value)}
                                renderInput={(params) => <TextField {...params} size="small" placeholder="Select product" />}
                                renderOption={(props, option) => (
                                  <li {...props}>
                                    <Box>
                                      <Typography variant="body2">{option.name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {option.hasVariants ? 'Has Variants' : `SKU: ${option.sku}`}
                                      </Typography>
                                    </Box>
                                  </li>
                                )}
                                sx={{ minWidth: { xs: 140, sm: 180 } }}
                                disabled={loading}
                              />
                              )}
                              {/* Show variant selector on mobile below product */}
                              {hasVariants && (
                                <Autocomplete
                                  options={variants}
                                  getOptionLabel={(option) => option.variantSelections.map(vs => vs.variantValue).join(' - ')}
                                  value={variants.find(v => v.id === item.variantId) || null}
                                  onChange={(_, value) => handleVariantSelect(index, value)}
                                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select variant" />}
                                  sx={{ minWidth: { xs: 140, sm: 180 }, mt: 1, display: { xs: 'block', sm: 'none' } }}
                                  disabled={loading}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                              {isRawMaterial ? (
                                <TextField
                                  size="small"
                                  placeholder="Unit (kg, m, pcs)"
                                  value={item.unit || ''}
                                  onChange={(e) => handleItemChange(index, 'unit', e.target.value)}
                                  sx={{ width: 120 }}
                                  disabled={loading}
                                />
                              ) : hasVariants ? (
                                <Autocomplete
                                  options={variants}
                                  getOptionLabel={(option) => option.variantSelections.map(vs => vs.variantValue).join(' - ')}
                                  value={variants.find(v => v.id === item.variantId) || null}
                                  onChange={(_, value) => handleVariantSelect(index, value)}
                                  renderInput={(params) => <TextField {...params} size="small" placeholder="Select variant" />}
                                  renderOption={(props, option) => (
                                    <li {...props}>
                                      <Box>
                                        <Typography variant="body2">{option.variantSelections.map(vs => vs.variantValue).join(' - ')}</Typography>
                                        <Typography variant="caption" color="text.secondary">SKU: {option.sku} | {formatCurrency(option.price, currencySymbol)}</Typography>
                                      </Box>
                                    </li>
                                  )}
                                  sx={{ minWidth: 160 }}
                                  disabled={loading}
                                />
                              ) : (
                                <Typography variant="body2" color="text.secondary">-</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                              {isRawMaterial ? (
                                <TextField
                                  size="small"
                                  placeholder="SKU / code"
                                  value={item.sku}
                                  onChange={(e) => handleItemChange(index, 'sku', e.target.value)}
                                  sx={{ width: 110 }}
                                  disabled={loading}
                                />
                              ) : (
                                <Typography variant="body2">{item.sku || '-'}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number" size="small" value={item.quantity}
                                onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                                inputProps={{ min: 1, style: { fontWeight: 600, color: theme.palette.text.primary } }}
                                sx={{ width: 90 }} disabled={loading}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                type="number" size="small" value={item.unitPrice}
                                onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, step: 0.01, style: { fontWeight: 600, color: theme.palette.text.primary } }}
                                sx={{ width: 120 }} disabled={loading}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                {formatCurrency(item.totalPrice, currencySymbol)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" color="error" onClick={() => handleRemoveItem(index)} disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {items.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                              No items added. Click "Add Item" to get started.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>

              {/* Financial */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Tax Rate (%)" type="number" value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }} disabled={loading} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={`Shipping Cost (${currencySymbol})`} type="number" value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  inputProps={{ min: 0, step: 0.01 }} disabled={loading} />
              </Grid>

              {/* Totals */}
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                  }}
                >
                  <Grid container spacing={1}>
                    {[
                      { label: 'Subtotal:', value: formatCurrency(totals.subtotal, currencySymbol) },
                      { label: `Tax (${taxRate}%):`, value: formatCurrency(totals.tax, currencySymbol) },
                      { label: 'Shipping:', value: formatCurrency(totals.shipping, currencySymbol) },
                    ].map(({ label, value }) => (
                      <React.Fragment key={label}>
                        <Grid item xs={6}><Typography variant="body2">{label}</Typography></Grid>
                        <Grid item xs={6} textAlign="right"><Typography variant="body2">{value}</Typography></Grid>
                      </React.Fragment>
                    ))}
                    <Grid item xs={6}><Typography variant="h6">Total:</Typography></Grid>
                    <Grid item xs={6} textAlign="right">
                      <Typography variant="h6" color="primary">{formatCurrency(totals.total, currencySymbol)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Date & Notes */}
              <Grid item xs={12}>
                <TextField fullWidth label="Expected Delivery Date" type="date" value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} disabled={loading} />
              </Grid>
              {/* <Grid item xs={12}>
                <TextField fullWidth label="Notes (Visible to Supplier)" multiline rows={3} value={notes}
                  onChange={(e) => setNotes(e.target.value)} disabled={loading} />
              </Grid> */}
              <Grid item xs={12}>
                <TextField fullWidth label="Internal Notes" multiline rows={2} value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)} disabled={loading} />
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Divider />
          <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, justifyContent: 'flex-end', flexShrink: 0 }}>
            <Button onClick={handleClose} disabled={loading} startIcon={<CancelIcon />}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !selectedSupplier || items.length === 0}
              startIcon={<SaveIcon />}
            >
              Create Purchase Order
            </Button>
          </Box>
        </Box>
      </Drawer>

      <SupplierDialog
        open={supplierDialogOpen}
        onClose={() => setSupplierDialogOpen(false)}
        onSuccess={handleSupplierDialogSuccess}
        onShowToast={onShowToast}
      />

      {/* ── Full-screen saving overlay ─────────────────────────────────────── */}
      <Backdrop
        open={loading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Creating Purchase Order…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>
      {/* ────────────────────────────────────────────────────────────────────── */}
    </>
  );
};

export default POCreateDialog;